import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import { ghs } from '../lib/money.js';
import { reportToCsv, downloadCsv } from '../lib/export.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const SEVERITY = {
  denied:  { tone: 'danger',  label: 'Refused',  blurb: 'Somebody was stopped' },
  serious: { tone: 'warning', label: 'Serious',  blurb: 'Money or permissions' },
  notable: { tone: 'info',    label: 'Notable',  blurb: 'Created or changed' },
  routine: { tone: 'neutral', label: 'Routine',  blurb: 'Selling and signing in' }
};

const when = t => new Date(t).toLocaleString('en-GB',
  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = n => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

/**
 * The audit log.
 *
 * Two halves, and the order matters. The top is what somebody should look at without being
 * asked: refusals, doors tried, and everything that touched money. The bottom is the whole
 * record, searchable. A log you have to interrogate is a log nobody opens.
 */
export function AuditLog({ canRead }) {
  const [tab, setTab] = useState('attention');
  const [attention, setAttention] = useState(null);
  const [meta, setMeta] = useState({ people: [], actions: [], businesses: [] });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canRead) return;
    api.auditAttention(7).then(setAttention).catch(e => setError(e.data?.error || 'failed'));
    api.auditPeople().then(setMeta).catch(() => {});
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="card" style={{ padding: 40, display: 'grid', gap: 8, placeItems: 'center', textAlign: 'center' }}>
        <div className="t-heading-m">The audit log is not part of your role</div>
        <div className="t-body-m muted" style={{ maxWidth: 560 }}>
          It opens at admin. This attempt was itself recorded.
        </div>
      </div>
    );
  }

  if (error) return <Banner tone="danger">The audit log did not load.</Banner>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div className="t-body-m secondary" style={{ flex: 1 }}>
          Every action, who did it, and what it changed. Append only: nothing here can be
          edited or removed, including by a super admin.
        </div>
        {['attention', 'everything'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{
              padding: '9px 16px', cursor: 'pointer', font: 'inherit',
              border: '1px solid ' + (tab === t ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
              background: tab === t ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
              color: tab === t ? 'var(--color-accent-strong)' : 'inherit',
              borderRadius: 'var(--radius-md)', fontWeight: tab === t ? 600 : 500
            }}>
            {t === 'attention' ? 'Needs a look' : 'Everything'}
          </button>
        ))}
      </div>

      {tab === 'attention'
        ? <Attention data={attention} onPerson={() => setTab('everything')} />
        : <Everything meta={meta} />}
    </div>
  );
}

// ---------------------------------------------------------------- needs a look

function Attention({ data, onPerson }) {
  if (!data) return <div className="t-body-m muted">Reading the last week</div>;

  const nothing = !data.deniedByPerson.length && !data.wrongDoors.length
    && !data.failedSignIns.length && !data.serious.length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="fig-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {['denied', 'serious', 'notable', 'routine'].map(s => (
          <div key={s} style={{ ...card, padding: 14 }}>
            <Chip tone={SEVERITY[s].tone}>{SEVERITY[s].label}</Chip>
            <div className="money t-money-l" style={{ marginTop: 6 }}>{data.counts[s] || 0}</div>
            <div className="t-body-s muted">{SEVERITY[s].blurb}, last {data.days} days</div>
          </div>
        ))}
      </div>

      {nothing && (
        <div style={{ ...card, padding: 40, textAlign: 'center' }} className="t-body-m muted">
          Nothing refused, nothing serious, in the last {data.days} days.
        </div>
      )}

      {data.wrongDoors.length > 0 && (
        <Table title="DOORS TRIED, AND BY WHOM"
          note="Opening a business you do not hold. Once is a misclick. Repeatedly is not."
          rows={data.wrongDoors}
          cols={[
            ['person', r => `${r.username} (${r.role})`, 1],
            ['business', r => r.business || 'unknown', 200],
            ['attempts', r => r.attempts, 90, r => r.attempts > 2],
            ['last', r => when(r.last_at), 170]
          ]} />
      )}

      {data.deniedByPerson.length > 0 && (
        <Table title="REFUSED, BY PERSON"
          note="Everything each account was stopped from doing, whatever the reason."
          rows={data.deniedByPerson}
          cols={[
            ['person', r => `${r.username} (${r.role})`, 1],
            ['refusals', r => r.attempts, 90, r => r.attempts > 5],
            ['last', r => when(r.last_at), 170]
          ]} />
      )}

      {data.failedSignIns.length > 0 && (
        <Table title="SIGN INS THAT FAILED"
          note="The other way somebody probes a system. A locked account shows up here first."
          rows={data.failedSignIns}
          cols={[
            ['username tried', r => r.username || 'unknown', 1],
            ['attempts', r => r.attempts, 90, r => r.attempts >= 5],
            ['last', r => when(r.last_at), 170]
          ]} />
      )}

      {data.serious.length > 0 && (
        <div style={card}>
          <Head>MONEY AND PERMISSIONS, LAST {data.days} DAYS</Head>
          {data.serious.map(e => <Entry key={e.id} entry={e} />)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- everything

function Everything({ meta }) {
  const [q, setQ] = useState('');
  const [severity, setSeverity] = useState([]);
  const [userId, setUserId] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [includeViews, setIncludeViews] = useState(false);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);

  const params = useMemo(() => {
    const p = { limit: 100, offset };
    if (q.trim()) p.q = q.trim();
    if (severity.length) p.severity = severity.join(',');
    if (userId) p.userId = userId;
    if (businessId) p.businessId = businessId;
    if (from) p.from = from;
    if (to) p.to = to;
    if (includeViews) p.includeViews = '1';
    return p;
  }, [q, severity, userId, businessId, from, to, includeViews, offset]);

  useEffect(() => {
    const t = setTimeout(() => api.audit(params).then(setData).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [params]);

  useEffect(() => { setOffset(0); }, [q, severity, userId, businessId, from, to, includeViews]);

  const toggle = s => setSeverity(v => v.includes(s) ? v.filter(x => x !== s) : [...v, s]);

  const exportAll = () => {
    if (!data) return;
    const rows = [
      ['When', 'Who', 'Role', 'Severity', 'Outcome', 'Action', 'Screen', 'Summary', 'Detail'].join(','),
      ...data.entries.map(e => [
        e.created_at, e.username || '', e.role || '', e.severity, e.outcome, e.action,
        e.screen || '', e.summary, e.detail ? JSON.stringify(e.detail) : ''
      ].map(v => /[",\r\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : v).join(','))
    ].join('\r\n');
    downloadCsv(`audit-log-${from}-to-${to}`, rows);
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...card, padding: 14, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 260px' }}>
            <Field id="auditq" label="SEARCH" value={q} placeholder="a name, a word, an action"
              onChange={e => setQ(e.target.value)} />
          </div>
          <Select label="PERSON" value={userId} onChange={setUserId}
            options={[['', 'anybody'], ...meta.people.map(p => [p.id, `${p.username} (${p.entries})`])]} />
          <Select label="BUSINESS" value={businessId} onChange={setBusinessId}
            options={[['', 'all six'], ...meta.businesses.map(b => [b.id, b.short_name])]} />
          <div style={{ width: 150 }}>
            <Field id="from" label="FROM" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div style={{ width: 150 }}>
            <Field id="to" label="TO" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {Object.keys(SEVERITY).map(s => {
            const on = severity.includes(s);
            return (
              <button key={s} onClick={() => toggle(s)}
                style={{
                  padding: '6px 12px', cursor: 'pointer', font: 'inherit', fontSize: 13,
                  border: '1px solid ' + (on ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                  background: on ? 'var(--color-accent-subtle)' : 'transparent',
                  borderRadius: 'var(--radius-full)', fontWeight: on ? 600 : 400
                }}>
                {SEVERITY[s].label}
                {data?.bySeverity?.[s] ? ` ${data.bySeverity[s]}` : ''}
              </button>
            );
          })}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }} className="t-body-s muted">
            <input type="checkbox" checked={includeViews} onChange={e => setIncludeViews(e.target.checked)} />
            include screens opened
          </label>
          <span style={{ flex: 1 }} />
          <span className="t-body-s muted">{data ? `${data.total} entries` : ''}</span>
          <Button size="m" variant="secondary" onClick={exportAll} disabled={!data?.entries.length}>
            Export this page
          </Button>
        </div>
      </div>

      <div style={card}>
        {!data && <div style={{ padding: 30 }} className="t-body-m muted">Searching</div>}
        {data && !data.entries.length && (
          <div style={{ padding: 40, textAlign: 'center' }} className="t-body-m muted">
            Nothing matches. The log holds everything, so a blank result means the filter, not a gap.
          </div>
        )}
        {data?.entries.map(e => <Entry key={e.id} entry={e} />)}
      </div>

      {data && data.total > data.entries.length && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button size="m" variant="secondary" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 100))}>Newer</Button>
          <span className="t-body-s muted" style={{ alignSelf: 'center' }}>
            {offset + 1} to {offset + data.entries.length} of {data.total}
          </span>
          <Button size="m" variant="secondary" disabled={offset + 100 >= data.total}
            onClick={() => setOffset(offset + 100)}>Older</Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one entry

function Entry({ entry: e }) {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY[e.severity] || SEVERITY.routine;
  const changes = isChangeSet(e.detail) ? e.detail : null;

  return (
    <div style={{
      borderTop: '1px solid var(--color-border-default)',
      borderLeft: e.severity === 'denied' ? '3px solid var(--color-status-danger)'
        : e.severity === 'serious' ? '3px solid var(--color-status-warning)' : '3px solid transparent'
    }}>
      <button onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', gap: 12, alignItems: 'center', padding: '11px 14px',
          background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit',
          color: 'inherit', textAlign: 'left'
        }}>
        <span className="money t-money-s muted" style={{ width: 155, flex: 'none' }}>{when(e.created_at)}</span>
        <span className="t-body-m" style={{ width: 120, flex: 'none' }}>{e.username || 'system'}</span>
        <Chip tone={sev.tone}>{e.action}</Chip>
        <span className="t-body-m secondary" style={{
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>{e.summary}</span>
        {changes && <Chip tone="info" showDot={false}>{Object.keys(changes).length} changed</Chip>}
        {e.screen && <span className="t-body-s muted" style={{ flex: 'none' }}>{e.screen}</span>}
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px 14px', display: 'grid', gap: 10, background: 'var(--color-bg-subtle)' }}>
          <div style={{ paddingTop: 12, display: 'flex', gap: 20, flexWrap: 'wrap' }} className="t-body-s muted">
            <span>{e.role || 'no role'}</span>
            {e.method && <span>{e.method} {e.path}</span>}
            {e.entity && <span>{e.entity} {e.entity_id}</span>}
            <span>{e.outcome}</span>
          </div>

          {changes
            ? <Changes changes={changes} />
            : e.detail && (
              <pre className="t-body-s" style={{
                margin: 0, padding: 12, background: 'var(--color-bg-surface)',
                borderRadius: 'var(--radius-md)', overflowX: 'auto', whiteSpace: 'pre-wrap'
              }}>{JSON.stringify(e.detail, null, 2)}</pre>
            )}
        </div>
      )}
    </div>
  );
}

/** A detail blob shaped { field: { from, to } } is a diff and gets rendered as one. */
const isChangeSet = d =>
  d && typeof d === 'object' && !Array.isArray(d) && Object.keys(d).length > 0 &&
  Object.values(d).every(v => v && typeof v === 'object' && 'from' in v && 'to' in v);

function Changes({ changes }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {Object.entries(changes).map(([field, { from, to }]) => (
        <div key={field} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="t-label-xs muted" style={{ width: 150, flex: 'none' }}>
            {field.replace(/_/g, ' ').toUpperCase()}
          </span>
          <span className="money t-money-s" style={{
            padding: '3px 8px', borderRadius: 'var(--radius-sm)',
            background: 'var(--color-status-danger-bg)', color: 'var(--color-status-danger)',
            textDecoration: 'line-through'
          }}>{format(field, from)}</span>
          <span className="muted">to</span>
          <span className="money t-money-s" style={{
            padding: '3px 8px', borderRadius: 'var(--radius-sm)',
            background: 'var(--color-status-success-bg)', color: 'var(--color-status-success)'
          }}>{format(field, to)}</span>
        </div>
      ))}
    </div>
  );
}

/** Money is stored in pesewas, so a price diff must not read as "30000 to 27550". */
function format(field, value) {
  if (value === null || value === undefined) return 'nothing';
  if (/price|amount|total|commission_amount|float|variance/.test(field) && typeof value === 'number') {
    return ghs(value);
  }
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

// ---------------------------------------------------------------- bits

const Head = ({ children }) => (
  <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
    {children}
  </div>
);

function Table({ title, note, rows, cols }) {
  return (
    <div style={card}>
      <Head>{title}</Head>
      {note && <div style={{ padding: '8px 16px' }} className="t-body-s muted">{note}</div>}
      {rows.map((r, i) => (
        <div key={i} style={{
          display: 'flex', gap: 12, alignItems: 'center', padding: '11px 16px',
          borderTop: '1px solid var(--color-border-default)'
        }}>
          {cols.map(([, get, width, alarm], j) => {
            const loud = alarm && alarm(r);
            return (
              <span key={j}
                className={j === 0 ? 't-body-m' : 'money t-money-s'}
                style={width === 1
                  ? { flex: 1, minWidth: 0 }
                  : { width, flex: 'none', textAlign: 'right',
                      color: loud ? 'var(--color-status-danger)' : undefined,
                      fontWeight: loud ? 700 : undefined }}>
                {get(r)}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="field" style={{ width: 180 }}>
      <label className="t-label-xs">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{
          height: 48, padding: '0 10px', borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-strong)', background: 'var(--color-bg-surface)',
          color: 'var(--color-text-primary)', font: '400 15px/22px var(--font-ui)'
        }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
